<template>
    <div class="player-category-card">
        <div class="category-header">
            <div class="category-title-row">
                <h3 class="category-title">{{ category.title }}</h3>
                <div class="category-controls">
                    <UiSelect
                        v-model="selectedPositions"
                        label="Позиции"
                        :options="positionOptions"
                        multiple
                        show-select-all
                        :disabled="loading"
                        :block="false"
                        align="left"
                        value-type="string"
                        :summary-fn="positionsSummary"
                        @change="onPositionsChange"
                    />
                    <UiSelect
                        v-model="selectedSort"
                        label="Показатель"
                        :options="category.sortOptions"
                        :disabled="loading"
                        :block="false"
                        align="left"
                        value-type="string"
                        @change="onSortChange"
                    />
                </div>
            </div>
        </div>

        <div class="category-content-wrapper" :class="{ 'is-loading': loading }">
            <div v-if="loading" class="loading-overlay">
                <div class="spinner-large"></div>
            </div>

            <div v-if="data && data.top" class="category-content">
                <!-- Большая карточка лучшего игрока -->
                <TopPlayerCard :player="data.top" :category="category.key" :sort-by="selectedSort" />

                <!-- Список игроков 2-5 -->
                <div class="players-list" style="position: relative;">
                    <template v-for="(player, index) in data.list" :key="player.id || `ellipsis-${index}`">
                        <div v-if="player.is_ellipsis" class="players-list-ellipsis">
                            <span class="ellipsis-dots">•••</span>
                        </div>
                        <PlayerListItem v-else :player="player" :category="category.key"
                            :rank="getPlayerRank(player, index)" :sort-by="selectedSort" />
                    </template>
                </div>
            </div>

            <div v-else class="no-data">
                <p>Нет данных для отображения</p>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue';
import axios from 'axios';
import TopPlayerCard from './TopPlayerCard.vue';
import PlayerListItem from './PlayerListItem.vue';
import UiSelect from '@/components/ui/forms/UiSelect.vue';

const props = defineProps({
    category: {
        type: Object,
        required: true
    },
    data: {
        type: Object,
        default: () => ({ top: null, list: [] })
    },
    currentSeasonId: {
        type: Number,
        default: null
    },
    positions: {
        type: Array,
        default: () => []
    },
    selectedPositions: {
        type: Array,
        default: () => []
    },
    selectedCompetitions: {
        type: Array,
        default: () => []
    },
    initialSort: {
        type: String,
        default: null
    }
});

const emit = defineEmits(['sort-change', 'positions-change', 'data-updated']);

const selectedSort = ref(props.initialSort || props.category.sortOptions[0]?.value || 'kills');
const loading = ref(false);

// Определяем, какие позиции доступны для этой категории (исключаем либеро для атаки, подачи и блока)
const availablePositions = computed(() => {
    const categoriesWithoutLibero = ['attack', 'serve', 'block'];
    if (categoriesWithoutLibero.includes(props.category.key)) {
        return props.positions.filter(p => p.slug !== 'libero');
    }
    return props.positions;
});

const positionOptions = computed(() =>
    availablePositions.value.map((position) => ({
        value: position.slug,
        label: position.name,
    }))
);

// Инициализируем selectedPositions, исключая либеро для категорий без него
const getInitialPositions = () => {
    if (props.category.key === 'reception') {
        // Для приема по умолчанию только доигровщики и либеро
        return availablePositions.value
            .filter(p => p.slug === 'outside_hitter' || p.slug === 'libero')
            .map(p => p.slug);
    }
    return availablePositions.value.map(p => p.slug);
};

const selectedPositions = ref(props.selectedPositions || getInitialPositions());

watch(() => props.initialSort, (newSort) => {
    if (newSort) {
        selectedSort.value = newSort;
    }
}, { immediate: true });

watch(() => props.selectedPositions, (newPositions) => {
    if (newPositions && newPositions.length > 0) {
        // Фильтруем либеро для категорий без него
        const categoriesWithoutLibero = ['attack', 'serve', 'block'];
        if (categoriesWithoutLibero.includes(props.category.key)) {
            selectedPositions.value = newPositions.filter(pos => pos !== 'libero');
        } else {
            selectedPositions.value = [...newPositions];
        }
    } else {
        // Если позиции не переданы, используем начальные позиции
        selectedPositions.value = getInitialPositions();
    }
}, { immediate: true });

function positionsSummary(ids, options) {
    if (!ids.length) {
        return 'Нет позиций';
    }
    if (options.length && ids.length === options.length) {
        return 'Все позиции';
    }
    if (ids.length === 1) {
        const option = options.find((item) => item.value === ids[0]);
        return option?.label || '1 позиция';
    }
    return `${ids.length} позиций`;
}

const onSortChange = () => {
    emit('sort-change', props.category.key, selectedSort.value);
    loadCategoryData();
};

const onPositionsChange = (value) => {
    const next = Array.isArray(value) ? value : selectedPositions.value;
    selectedPositions.value = [...next];
    emit('positions-change', props.category.key, selectedPositions.value);
    loadCategoryData();
};

// Вычисляем ранг игрока (учитывая троеточие)
const getPlayerRank = (player, index) => {
    // Если у игрока уже есть ранг (например, для текущего пользователя вне топ-5), используем его
    if (player.rank) {
        return player.rank;
    }
    // Иначе вычисляем ранг: считаем количество игроков до этого индекса (исключая троеточие) + 2
    if (!props.data || !props.data.list) return index + 2;
    let rank = 2;
    for (let i = 0; i < index; i++) {
        if (!props.data.list[i]?.is_ellipsis) {
            rank++;
        }
    }
    return rank;
};

const loadCategoryData = async () => {
    if (props.currentSeasonId === null || loading.value) return;

    loading.value = true;

    try {
        const params = {
            season_id: props.currentSeasonId,
            [`${props.category.key}_sort`]: selectedSort.value,
            [`${props.category.key}_positions`]: selectedPositions.value,
        };

        // Добавляем выбранные турниры
        if (props.selectedCompetitions && props.selectedCompetitions.length > 0) {
            params['competitions'] = props.selectedCompetitions;
        }

        // Загружаем только данные для этой категории
        const response = await axios.get('/api/best-players', { params });

        // Обновляем данные через emit
        emit('data-updated', props.category.key, response.data[props.category.key]);
    } catch (error) {
        console.error('Error loading category data:', error);
    } finally {
        loading.value = false;
    }
};
</script>

<style lang="scss" scoped>
@import '../../../scss/tokens';

.player-category-card {
    background: $color-bg-primary;
    border: 1px solid $color-brand-alpha;
    border-radius: $radius-lg;
    padding: $space-4;
    box-shadow: $shadow;

    .category-header {
        margin-bottom: 1rem;

        .category-title-row {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 1rem;
            margin-bottom: 0.75rem;

            .category-title {
                font-size: $font-size-base;
                font-weight: 600;
                color: $color-brand;
                margin: 0;
                flex: 1;
            }

            .category-controls {
                display: flex;
                align-items: center;
                flex-wrap: wrap;
                justify-content: flex-end;
                gap: 0.5rem;
            }
        }
    }

    .category-content-wrapper {
        position: relative;
        min-height: 200px;

        &.is-loading {
            pointer-events: none;
            user-select: none;
        }

        .loading-overlay {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(255, 255, 255, 0.8);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 10;
            border-radius: $radius-md;

            .spinner-large {
                width: 40px;
                height: 40px;
                border: 3px solid $color-brand-alpha;
                border-top-color: $color-brand;
                border-radius: 50%;
                animation: spin 0.8s linear infinite;
            }
        }

        .category-content {
            display: flex;
            flex-direction: column;
            gap: 1rem;

            .players-list {
                display: flex;
                flex-direction: column;
                gap: 0.75rem;
                overflow: visible;

                .players-list-ellipsis {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 0.5rem 0;
                    min-height: 1.5rem;

                    .ellipsis-dots {
                        font-size: 1.2rem;
                        color: $color-text-muted;
                        letter-spacing: 0.2rem;
                        font-weight: 300;
                    }
                }
            }
        }

        .no-data {
            text-align: center;
            padding: 1.5rem;
            color: $color-text-muted;
            font-size: 0.7rem;
        }
    }

    @include down($bp-md) {
        .category-header .category-title-row {
            align-items: flex-start;
            flex-direction: column;

            .category-controls {
                width: 100%;
                justify-content: flex-start;
            }
        }
    }

    @keyframes spin {
        to {
            transform: rotate(360deg);
        }
    }
}
</style>
